/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useReducer, useState, useCallback } from 'react';
import { each, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import styled from 'styled-components';
// @material-ui/core
import Checkbox from '@material-ui/core/Checkbox';
import Modal from '@material-ui/core/Modal';
import withStyles from '@material-ui/core/styles/withStyles';
// components
import Button from 'components/Button/Button';
import ToggleComponent from 'components/Toggle/Toggle';
import Typography from 'components/Typography/Typography';
import { VSpacer } from 'components/Spacer/Spacer';
// views
import DebugComponent from './DebugComponent';
import ImageControls from './ImageControls/ImageControls';
import Photos from './Photos';
import SKUTable from './SKUTable';
import ShelfVisualizer from '@infilect_developer/shelf-visualiser';
import { track } from 'analytics';
import { DQS_EVENTS } from 'analytics/events';
import useReasonCodes, {
  buildManualQaReviewList,
  useSessionReviewStatus
} from './useReasonCodes';
import { unstable_batchedUpdates } from 'react-dom';
import { groupByBrandCategorySubcategory } from 'utils/groupByBrandCategorySubcategory';
import {
  brandBlockingTabKey,
  skuBlockingTabKey
} from 'pages/GenericSE/GenericSE';
import { priorityColorJSON } from 'utils/priorityColor';

const styles = theme => ({
  modalRoot: {
    overflow: 'scroll'
  },
  modalContent: {
    color: theme.palette.type === 'light' ? ' #767676 ' : '#94949a',
    backgroundColor: theme.palette.type === 'light' ? '#fff' : '#1e1e2b',
    margin: 100,
    padding: 100,
    outline: 'none'
  },
  checkbox: {
    color: theme.palette.type === 'light' && '#0f0d36 !important'
  },
  button: {
    background: theme.palette.type === 'light' ? '#18c7fe' : 'rgb(39, 42, 61)',
    color: theme.palette.type === 'light' && 'white',

    '&:hover': {
      background:
        theme.palette.type === 'light' ? '#18c7fe' : 'hsla(0, 1%, 28%, 0.2)',
      color: theme.palette.type === 'light' && 'white'
    },
    '&:focus': {
      background: theme.palette.type === 'light' ? '#18c7fe' : '#ffffff33',
      color: theme.palette.type === 'light' && 'white'
    }
  },
  flex: {
    display: 'flex',
    alignItems: 'baseline'
  },
  infoCheckbox: {
    marginLeft: 'auto',
    alignItems: 'center',
    '& h4': {
      color: 'rgba(0,0,0,0.87)'
    }
  },
  modeToggle: {
    '& button': {
      marginLeft: 10
    }
  },
  signal: {
    width: 30,
    height: 30
  },
  problem: {
    marginRight: 10
  },
  checkBoxGroup: {
    alignItems: theme.palette.type === 'light' && 'center',
    '& h4': {
      color: theme.palette.type === 'light' && '#767676'
    }
  },
  sessionIdWrapper: {
    [theme.breakpoints.up('xl')]: {
      maxWidth: 'none'
    },
    maxWidth: '12vw',
    paddingLeft: '10px'
  },
  sessionIdText: {
    marginLeft: 'auto',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  }
});

const initialState = {
  activeBoxes: [],
  correctionData: [],
  activeMode: 'include',
  tableData: {},
  activeControls: {}
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'UPDATE_ACTIVE_BOXES':
      return { ...state, activeBoxes: action.payload };
    case 'UPDATE_ACTIVE_CONTROLS':
      return { ...state, activeControls: action.payload };
    case 'CHANGE_MODE':
      return { ...state, activeMode: action.payload };
    case 'UPDATE_TABLE_DATA':
      return { ...state, tableData: action.payload };
    case 'UPDATE_MULTIPLE':
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

let selectedRows = {};

const controls = ['zoomIn', 'reset'];

const checkboxes = [{ name: 'showStickers', title: 'Show Stickers' }];

const StyledCheckbox = styled(Checkbox)`
  && {
    color: ${props => `${props.checkboxColor} !important`};
  }
`;

// eslint-disable-next-line react/prefer-stateless-function
const TestStoreExplorerPage = ({
  photosList,
  tableData,
  productFilter,
  saveDebug,
  handleDataDownload,
  updateProductFacing,
  variantList,
  dashboardType,
  tseWorkflow,
  shelfData,
  saveFacings,
  sessionInfo,
  sessionName,
  sessionDate,
  fixProductFacingAPIStatus,
  updateProductFacingAPIStatus,
  dispatchUpdateProductFacing,
  handleTseCorrectionCompleted,
  manualQA,
  clientPreferences,
  chartData,
  chartList,
  layoutCSS,
  dashboardConfig,
  fetchOptionsForNewPatternDetectionSchema,
  postNewPatternDetection,
  checkNewPatternExistence
}) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [isModalOpen, setModalState] = useState(false);
  const [toggleState, setToggleState] = useState(false);
  const [checkBoxState, setCheckBoxState] = useState({});
  const [isCropInfoHandlerSelected, setCropInfoHandlerSelected] = useState(
    false
  );
  const { reasonCodes } = useReasonCodes();
  const [staticChartData, setStaticChartData] = useState({});
  const [staticCartList, setStaticCartList] = useState([]);

  const { sessionReviewStatus } = useSessionReviewStatus(
    shelfData,
    reasonCodes
  );

  console.log('Shelfdata', shelfData);

  const updateFacing = useCallback(data => {
    const newBoxes = data.filter(item => !item.isSaved);
    const formattedParams = newBoxes.map(box => {
      let obj = {};
      obj.box_id = box.id;
      obj.id = box.variantId;
      if (tseWorkflow === 'v2') {
        obj.photo_id = box.photoID;
      }
      obj.include = box.inclusion;
      obj.is_exclusion = box.is_exclusion;
      obj.is_overlap = box.is_overlap;
      return obj;
    });

    const paramsObj = {
      session_id: shelfData?.session_data?.[0],
      photo_id: [],
      updated_data: {
        facing_ids: [],
        include: true,
        is_exclusion: false,
        is_overlap: false
      }
    };

    if (tseWorkflow === 'v2' && formattedParams.length > 0) {
      const tempParam = formattedParams[0];
      paramsObj.updated_data.include = tempParam.include;
      paramsObj.updated_data.is_exclusion = tempParam.is_exclusion;
      paramsObj.updated_data.is_overlap = tempParam.is_overlap;

      formattedParams.forEach(res => {
        if (!paramsObj.photo_id.includes(res.photo_id)) {
          paramsObj.photo_id.push(res.photo_id);
        }
        if (!paramsObj.updated_data.facing_ids.includes(res.box_id)) {
          paramsObj.updated_data.facing_ids.push(res.box_id);
        }
      });
    }
    if (newBoxes.length) {
      track(DQS_EVENTS.SAVE_CORRECTIONS_CLICKED, {
        session_id: shelfData?.session_data?.[0],
        corrections_count: newBoxes.length,
        tse_workflow: tseWorkflow,
        facing_ids: paramsObj.updated_data.facing_ids,
      });
      dispatchUpdateProductFacing({
        params: tseWorkflow === 'v2' ? paramsObj : formattedParams,
        tseWorkflow
      });
    }
  });

  const markTseCorrectionsCompleted = useCallback(
    data => {
      if (manualQA && !data.reasons?.length) {
        return alert('Manual QA is enabled please select atleast one reason');
      }

      handleTseCorrectionCompleted(data);
    },
    [manualQA]
  );

  const toolbarConfig = [
    {
      name: 'viewInfo',
      type: 'checkbox',
      label: ['View Info'],
      data: 'options goes here',
      enabled: true
    },
    {
      name: 'inclusion',
      type: 'toggle',
      label: ['Include', 'Exclude'],
      enabled: true
    },
    {
      name: 'save',
      type: 'button',
      label: ['Save Corrections'],
      action: val => updateFacing(val),
      enabled: true
    },
    ...(manualQA
      ? [
          {
            name: 'acceptance-status',
            type: 'toggle',
            label: ['Accept', 'Reject'],
            acceptanceList: [],
            enabled: true
          }
        ]
      : []),
    ...(tseWorkflow === 'v2'
      ? [
          {
            name: 'complete',
            type: 'button',
            label: ['Save Survey'],
            onClick: data => {
              markTseCorrectionsCompleted(data);
            },
            enabled: true
          }
        ]
      : [])
  ];

  useEffect(() => {
    if (!saveDebug.isLoading && !saveDebug.error) {
      setModalState(false);
    }
  }, [saveDebug]);

  const getColorBasedOnBrandPriority = (detectionID, tabType) => {
    if (tabType === brandBlockingTabKey || tabType === skuBlockingTabKey) {
      let index = '20';
      const datasets = staticChartData[tabType]?.datasets || [];
      let tempIndex = datasets.findIndex(res =>
        res.detections.includes(detectionID)
      );

      if (tempIndex >= 0) {
        index = tempIndex;
      }

      if (index > 20) {
        index = 20;
      }
      index = index.toString();

      return priorityColorJSON[index];
    }
    return null;
  };

  useEffect(() => {
    if (shelfData.variants && clientPreferences?.se_web?.brand_blocking) {
      const brandBlockingData = groupByBrandCategorySubcategory(
        shelfData.variants
      );

      if (brandBlockingData.length > 0) {
        unstable_batchedUpdates(() => {
          setStaticCartList(prev => {
            const tempPrev = prev.filter(
              res => res.name !== brandBlockingTabKey
            );
            return [
              ...tempPrev,
              {
                params: [],
                type: 'table',
                name: brandBlockingTabKey,
                title: 'Brand Blocking',
                granularities: [],
                filters: [],
                slicers: [],
                excel_report: false
              }
            ];
          });
          setStaticChartData(prev => {
            return {
              ...prev,
              [brandBlockingTabKey]: {
                height: 520,
                title: 'Sku Secondary Display',
                datasets: brandBlockingData,
                columns: [
                  {
                    label: 'BRAND',
                    dataKey: 'brand'
                  },
                  {
                    label: 'CATEGORY',
                    dataKey: 'category'
                  },
                  {
                    label: 'SUB CATEGORY',
                    dataKey: 'subcategory'
                  },
                  {
                    label: 'COUNT',
                    dataKey: 'count'
                  },
                  {
                    label: 'COLOR-COL',
                    dataKey: 'color'
                  }
                ]
              }
            };
          });
        });
      }
    }
  }, [shelfData, clientPreferences]);

  useEffect(() => {
    dispatch({
      type: 'UPDATE_TABLE_DATA',
      payload: { ...tableData }
    });
  }, [tableData]);

  const handleModeToggle = () => {
    dispatch({
      type: 'CHANGE_MODE',
      payload: state.activeMode === 'exclude' ? 'include' : 'exclude'
    });
  };

  const handleCheckBoxClick = ({
    row,
    columns,
    isChecked,
    i: index,
    selectAll = false
  }) => {
    if (isChecked) {
      if (selectAll) {
        each(row, (r, i) => {
          selectedRows[`${i}t`] = r.bnBox.map(re => ({
            ...re,
            rowID: i,
            rowDetails: { ...r, bnBox: null },
            columns
          }));
        });
      } else {
        selectedRows[`${index}t`] = row.bnBox.map(re => ({
          ...re,
          rowDetails: { ...row, bnBox: null },
          columns,
          rowID: index
        }));
      }
    } else if (selectAll) {
      selectedRows = {};
    } else {
      delete selectedRows[`${index}t`];
    }
    let boxesCoords = [];
    each(selectedRows, r => {
      boxesCoords = [...boxesCoords, ...r];
    });
    dispatch({ type: 'UPDATE_ACTIVE_BOXES', payload: boxesCoords });
  };

  // const resetAnnotation = () => {
  //   dispatch({
  //     type: 'UPDATE_MULTIPLE',
  //     payload: {
  //       activeBoxes: [],
  //       tableData,
  //       correctionData: {}
  //     }
  //   });
  // };

  const saveAnnotation = () => {
    if (state.correctionData.length) {
      updateProductFacing({
        params: state.correctionData
      });
    }
    dispatch({
      type: 'UPDATE_MULTIPLE',
      payload: {
        correctionData: []
      }
    });
  };

  const handleCropClick = ({ crop }) => {
    const cropVariant = variantList.find(
      variant => variant.id === crop.rowDetails.variantId
    );
    const originalBox = cropVariant.bounding_boxes.find(
      box => box.id === crop.id
    );
    const clickedBoxes = state.correctionData.map(item => item.box_id);

    let correctionData = [...state.correctionData];

    const indexOfCorrectionData = correctionData
      .map(item => item.box_id)
      .indexOf(crop.id);

    const boxes = state.activeBoxes.map(box => {
      if (box.id === crop.id) {
        return {
          ...box,
          color:
            clickedBoxes.includes(crop.id) &&
            (correctionData[indexOfCorrectionData] || {}).include ===
              (state.activeMode !== 'exclude')
              ? originalBox.color
              : state.activeMode === 'exclude'
              ? '#FF0000'
              : '#44ec09'
        };
      }
      return box;
    });
    const datasets = [...new Set(state.tableData.datasets)];
    datasets[crop.rowID].bnBox = boxes;
    const updatedTableData = {
      ...state.tableData,
      datasets
    };

    if (indexOfCorrectionData < 0) {
      correctionData.push({
        box_id: crop.id,
        id: crop.rowDetails.variantId,
        include: state.activeMode !== 'exclude'
      });
    } else if (
      correctionData[indexOfCorrectionData].include !==
      (state.activeMode !== 'exclude')
    ) {
      correctionData.splice(indexOfCorrectionData, 1);
      correctionData.push({
        box_id: crop.id,
        id: crop.rowDetails.variantId,
        include: state.activeMode !== 'exclude'
      });
    } else {
      correctionData.splice(indexOfCorrectionData, 1);
    }
    dispatch({
      type: 'UPDATE_MULTIPLE',
      payload: {
        activeBoxes: boxes,
        tableData: updatedTableData,
        correctionData
      }
    });
  };

  const handleImageControls = ({ e, isActive }) => {
    switch (e) {
      case 'zoomIn':
        return dispatch({
          type: 'UPDATE_ACTIVE_CONTROLS',
          payload: { ...state.activeControls, [e]: !isActive }
        });
      case 'reset':
        return dispatch({
          type: 'UPDATE_ACTIVE_CONTROLS',
          payload: { ...state.activeControls, [e]: true }
        });
      default:
        return null;
    }
  };

  const handleCheckBoxChange = checkBox => {
    setCheckBoxState(p => ({
      ...p,
      [checkBox.name]: !p[checkBox.name]
    }));
  };

  return Object.keys(photosList).length > 0 &&
    Object.keys(state.tableData).length > 0 ? (
    <>
      {tseWorkflow === 'v2' && shelfData?.session_data?.length > 1 && (
        <div
          style={{
            width: '100%',
            padding: '10px 20px',
            background: '#2196F3',
            color: 'white',
            margin: '20px 0px'
          }}
        >
          <h2
            style={{
              marginBottom: '10px',
              color: 'white'
            }}
          >
            Alert!
          </h2>
          <p>
            More than one session id's received please add session id filter on
            left
          </p>
        </div>
      )}
      <ShelfVisualizer
        sessionData={shelfData}
        fontFamily={
          dashboardType === 'v2'
            ? 'loginPageFont'
            : `'Helvetica', 'Arial', sans-serif`
        }
        theme={dashboardType === 'v2' ? 'light' : 'dark'}
        tseWorkflow={tseWorkflow}
        seamApiKey={process.env.REACT_APP_SEAM_API_KEY}
        handleDataDownload={handleDataDownload}
        isTse={
          tseWorkflow === 'v2' && shelfData?.session_data?.length !== 1
            ? false
            : true
        }
        toolbarConfig={toolbarConfig}
        // subFeatureConfig={}
        fixFacingFilters={productFilter.data}
        saveFacings={saveFacings}
        sessionName={sessionName || ''}
        sessionDate={sessionDate || ''}
        shelfConfig={[{ key: 'sku_table_header', enable: false }]}
        isDisplay={sessionInfo.isDisplay}
        fixProductFacingAPIStatus={fixProductFacingAPIStatus}
        updateProductFacingAPIStatus={updateProductFacingAPIStatus}
        manualQaReviewList={
          manualQA && reasonCodes?.length
            ? buildManualQaReviewList(reasonCodes)
            : []
        }
        sessionReviewStatus={sessionReviewStatus}
        getColorBasedOnBrandPriority={getColorBasedOnBrandPriority}
        isNewPatternEnabled={
          clientPreferences?.se_web?.new_pattern_detection ?? false
        }
        fetchOptionsForNewPatternDetectionSchema={
          fetchOptionsForNewPatternDetectionSchema
        }
        onSubmitNewPatternDetection={postNewPatternDetection}
        checkNewPatternExistence={checkNewPatternExistence}
        tabsDetails={[
          { title: 'SKU Table', default: true, key: 'sku_table' },

          // ...(chartList.length > 0
          //   ? chartList.map((chart, index) => {
          //       return {
          //         title: chart.title,
          //         default: false,
          //         key: chart.name,
          //         data: chartData[chart.name],
          //         content: (
          //           <ChartRenderer
          //             chartList={[chartList[index]]}
          //             chartDataList={{
          //               [chart.name]: chartData[chart.name]
          //             }}
          //             cardStyles={layoutCSS?.theme?.card}
          //             dashboardPanelStyles={
          //               layoutCSS?.theme?.dashboardPanel
          //             }
          //             dashboardType={dashboardType}
          //             // handleDataDownload={handleChartDownload}
          //           />
          //         )
          //       };
          //     })
          //   : []),
          ...(staticCartList.length > 0
            ? staticCartList.map((chart, index) => {
                return {
                  title: chart.title,
                  default: false,
                  key: chart.name,
                  data: staticChartData[chart.name]
                };
              })
            : [])
        ]}
      />
    </>
  ) : null;
};

TestStoreExplorerPage.propTypes = {
  useExport: PropTypes.bool,
  useDisplayTag: PropTypes.bool,
  sessionId: PropTypes.string,
  stickerBoxes: PropTypes.array,
  photosList: PropTypes.array.isRequired,
  tableData: PropTypes.object.isRequired,
  productFilter: PropTypes.object.isRequired,
  fixProductFacingState: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
  debugProblems: PropTypes.array.isRequired,
  saveDebug: PropTypes.object.isRequired,
  handleDataDownload: PropTypes.func,
  updateProductFacing: PropTypes.func.isRequired,
  fixProductFacing: PropTypes.func.isRequired,
  handleDebugSave: PropTypes.func,
  primaryButtonStyles: PropTypes.object,
  cardStyles: PropTypes.object,
  dashboardPanelStyles: PropTypes.object,
  dashboardType: PropTypes.string,
  tooltipStyles: PropTypes.object,
  dropdownStyles: PropTypes.object,
  dropdownTitleStyles: PropTypes.object
};

TestStoreExplorerPage.defaultProps = {
  useExport: false,
  useDisplayTag: false,
  stickerBoxes: [],
  sessionId: '',
  handleDataDownload: () => {},
  handleDebugSave: () => {},
  primaryButtonStyles: {},
  cardStyles: {},
  dashboardPanelStyles: {},
  dashboardType: '',
  tooltipStyles: {},
  dropdownStyles: {},
  dropdownTitleStyles: {}
};

export default withStyles(styles)(TestStoreExplorerPage);
